import { getPsychologistWorkSchedules } from '../../../hooks/api/schedule';
import apiCoreAdapter from '../../../hooks/apiCoreAdapter';
import { mapBackendDayToWeekday, isoToLocalParts } from './utils';
import { utcTimeToLocalWithDayShift, utcTimeToLocal } from './workScheduleTimeHelpers';
import { SESSION_APPOINTMENT_TYPES } from '../../../hooks/api/config';
import { BA_TIMEZONE } from '../../../utils/timezone';

export type Patient = { name?: string; email: string; phone?: string; id?: string; process?: string; processType?: string; timezone?: string; nationality?: string; hasNextSessionDate?: boolean; hasPriorSessions?: boolean; isUrgent?: boolean; schedulingEligible?: boolean };

export type WorkSchedule = {
  id: string | number;
  psychologistId: string | number;
  weekday: number;
  startTime: string;
  endTime: string;
  active: boolean;
  scheduleType: 'standard' | 'reschedule_only';
};

export type SessionStatus =
  | 'confirmed'
  | 'pending'
  | 'cancelled'
  | 'scheduled'
  | 'active'
  | 'incomplete'
  | 'free'
  | 'settled'
  | 'lost'
  | 'blocked';

export type SessionWithPsychologist = {
  id: string;
  psychologistId: string | number;
  date: string;
  time: string;
  modality: string;
  status: SessionStatus;
  patient: { name?: string; email: string; phone?: string; timezone?: string };
  specialty?: string;
  origISO?: string;
  duration?: number;
  process_id?: string;
  /** Si es la primera sesión del paciente (mismo criterio que el chip "Primera vez" en Sesiones) */
  isFirst?: boolean;
  /**
   * IDs únicos (asignados por el backend) de los slots de bloqueo de 30min que componen
   * este bloqueo. Presente sólo cuando `status === 'blocked'` y el bloqueo viene del
   * fetch de disponibilidad (no en bloqueos "full-day" ni en el optimistic update recién
   * creado). Se usa para identificar sin ambigüedad qué borrar — nunca por fecha/hora/nombre,
   * que no son únicos cuando hay dos bloqueos iguales el mismo día.
   */
  blockageIds?: string[];
};

const schedulesCacheByPsych: Map<string, WorkSchedule[]> = new Map();
const patientsCacheByPsych: Map<string, Patient[]> = new Map();
const sessionsCacheByWeek: Map<string, SessionWithPsychologist[]> = new Map();
const pendingFetches: Map<string, Promise<unknown>> = new Map();

export function invalidateSchedulesCache(psychologistId?: string): void {
  if (psychologistId) {
    schedulesCacheByPsych.delete(psychologistId);
    const key = `schedules:${psychologistId}`;
    pendingFetches.delete(key);
  } else {
    schedulesCacheByPsych.clear();
    Array.from(pendingFetches.keys()).forEach(key => {
      if (key.startsWith('schedules:')) {
        pendingFetches.delete(key);
      }
    });
  }
}

export function invalidateSessionsCache(weekKey?: string): void {
  if (weekKey) {
    sessionsCacheByWeek.delete(weekKey);
    const key = `sessions:${weekKey}`;
    pendingFetches.delete(key);
  } else {
    sessionsCacheByWeek.clear();
    Array.from(pendingFetches.keys()).forEach(key => {
      if (key.startsWith('sessions:')) {
        pendingFetches.delete(key);
      }
    });
  }
}

export function invalidatePatientsCache(psychologistId?: string): void {
  if (psychologistId) {
    patientsCacheByPsych.delete(psychologistId);
    patientFullDataCacheByPsych.delete(psychologistId);
    leadProcessIdsCacheByPsych.delete(psychologistId);
    futureSessionProcessIdsCacheByPsych.delete(psychologistId);
    pendingFetches.delete(`patients:${psychologistId}`);
    pendingFetches.delete(`patients-full-data:${psychologistId}`);
    pendingFetches.delete(`lead-process-ids:${psychologistId}`);
    pendingFetches.delete(`future-session-process-ids:${psychologistId}`);
  } else {
    patientsCacheByPsych.clear();
    patientFullDataCacheByPsych.clear();
    leadProcessIdsCacheByPsych.clear();
    futureSessionProcessIdsCacheByPsych.clear();
    Array.from(pendingFetches.keys()).forEach(key => {
      if (key.startsWith('patients:') || key.startsWith('patients-full-data:') || key.startsWith('lead-process-ids:') || key.startsWith('future-session-process-ids:')) {
        pendingFetches.delete(key);
      }
    });
  }
}

// Item crudo de /patients/process-sessions-data (sin filtrar por status, todas las páginas).
export type PatientFullDataItem = Record<string, unknown> & {
  id: string;
  process_id?: string;
  last_session_date?: string | null;
  next_session_date?: string | null;
  scheduling_eligible?: boolean;
  status_buckets?: Array<'first' | 'active' | 'inactive' | 'withoutsession'>;
  /** Estado real del vínculo paciente-proceso (process_patient.status) — ver mapPatientStatus en usePatientsData.ts. */
  patient_status?: string | null;
  session_total?: number | null;
};

const patientFullDataCacheByPsych: Map<string, PatientFullDataItem[]> = new Map();

/**
 * Fetch único (todas las páginas, sin filtro de status) de process-sessions-data,
 * cacheado por psicóloga. Varios consumidores (fetchPatientsOnce, useMissingNextSession)
 * necesitan este mismo dataset con distintos recortes — antes cada uno lo pedía por
 * separado (con status=inactive / withoutSession / sin filtro), triplicando el llamado
 * a la agenda. status_buckets (agregado en el backend) permite clasificar en memoria.
 */
export async function fetchPatientFullDataOnce(psychologistId: string): Promise<PatientFullDataItem[]> {
  const cached = patientFullDataCacheByPsych.get(psychologistId);
  if (cached) return cached;
  const key = `patients-full-data:${psychologistId}`;
  const pending = pendingFetches.get(key) as Promise<PatientFullDataItem[]> | undefined;
  if (pending) return await pending;
  const promise = (async () => {
    const PER_PAGE = 200;
    const firstPage = await apiCoreAdapter.patientsInformation({ page: 1, per_page: PER_PAGE });
    const results = Array.isArray(firstPage?.results) ? [...firstPage.results] : [];
    const totalPages = firstPage?.metadata?.pages ?? 1;
    if (totalPages > 1) {
      const restPages = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          apiCoreAdapter.patientsInformation({ page: i + 2, per_page: PER_PAGE })
        )
      );
      for (const page of restPages) {
        if (Array.isArray(page?.results)) results.push(...page.results);
      }
    }
    const typed = results as unknown as PatientFullDataItem[];
    patientFullDataCacheByPsych.set(psychologistId, typed);
    pendingFetches.delete(key);
    return typed;
  })();
  pendingFetches.set(key, promise);
  return await promise;
}

// Nombres exactos de process_status del segmento "lead" (tabla process_status, ver
// schema): procesos que todavía no tuvieron su primera sesión con esta psicóloga, ya
// sea un lead nuevo o un ex-paciente que volvió y está de nuevo en este segmento.
const LEAD_STATUS_NAMES = [
  'Pendiente de asignar',
  '1er pago realizado',
  'Reasignar psicos',
  'Preseleccionar profesionales',
  '1ra sesión agendada',
  'En espera',
  'Horarios ofrecidos',
];

const leadProcessIdsCacheByPsych: Map<string, Set<string>> = new Map();

/**
 * IDs de proceso actualmente en un estado del segmento "lead". /patients/process-sessions-data
 * no expone el process_status crudo, pero sí acepta filtrar por su nombre exacto — se pide
 * cada uno de los 7 estados en paralelo (no acepta lista separada por comas) y se junta el
 * resultado. Usado para excluir del popup "Sin próxima sesión" a quienes están en LEAD
 * aunque tengan sesiones viejas (status_buckets los marcaría igual como "withoutsession").
 */
export async function fetchLeadProcessIds(psychologistId: string): Promise<Set<string>> {
  const cached = leadProcessIdsCacheByPsych.get(psychologistId);
  if (cached) return cached;
  const key = `lead-process-ids:${psychologistId}`;
  const pending = pendingFetches.get(key) as Promise<Set<string>> | undefined;
  if (pending) return await pending;
  const promise = (async () => {
    const pages = await Promise.all(
      LEAD_STATUS_NAMES.map((status) =>
        apiCoreAdapter.patientsInformation({ page: 1, per_page: 200, status }).catch(() => ({ results: [] }))
      )
    );
    const ids = new Set<string>();
    for (const page of pages) {
      for (const item of page?.results ?? []) {
        if (item.process_id) ids.add(item.process_id);
      }
    }
    leadProcessIdsCacheByPsych.set(psychologistId, ids);
    pendingFetches.delete(key);
    return ids;
  })();
  pendingFetches.set(key, promise);
  return await promise;
}

const futureSessionProcessIdsCacheByPsych: Map<string, Set<string>> = new Map();

/**
 * Process IDs con al menos una sesión futura no cancelada (próximos 90 días), leída directamente
 * de /sessions/by-day en vez de confiar en `next_session_date` de process-sessions-data. Ese campo
 * es un valor cacheado en el proceso y puede quedar desactualizado si algún flujo de agendamiento
 * no lo recalcula, mostrando el popup/formulario de "no agendamiento" a un paciente que en el
 * calendario claramente sí tiene su próxima sesión. Se usa como fuente de verdad adicional en
 * useMissingNextSession / useSessionsData antes de ofrecer ese formulario.
 */
export async function fetchFutureSessionProcessIds(psychologistId: string): Promise<Set<string>> {
  const cached = futureSessionProcessIdsCacheByPsych.get(psychologistId);
  if (cached) return cached;
  const key = `future-session-process-ids:${psychologistId}`;
  const pending = pendingFetches.get(key) as Promise<Set<string>> | undefined;
  if (pending) return await pending;
  const promise = (async () => {
    const now = new Date();
    const to = new Date(now);
    to.setDate(to.getDate() + 90);
    const from = now.toISOString().slice(0, 10);
    const toDate = to.toISOString().slice(0, 10);
    const byDay = await apiCoreAdapter.psychologistSessionsByDay({
      from: `${from} 00:00:00`,
      to: `${toDate} 23:59:59`,
      status: 'active,incomplete,settled',
    });
    const ids = new Set<string>();
    Object.values(byDay || {}).forEach((daySessions) => {
      if (!Array.isArray(daySessions)) return;
      daySessions.forEach((s) => {
        if (!s.isCancelled && s.process) ids.add(s.process);
      });
    });
    futureSessionProcessIdsCacheByPsych.set(psychologistId, ids);
    pendingFetches.delete(key);
    return ids;
  })();
  pendingFetches.set(key, promise);
  return await promise;
}

export async function fetchSchedulesOnce(psychologistId: string, timezone = BA_TIMEZONE): Promise<WorkSchedule[]> {
  const cached = schedulesCacheByPsych.get(psychologistId);
  if (cached) return cached;
  const key = `schedules:${psychologistId}`;
  const pending = pendingFetches.get(key) as Promise<WorkSchedule[]> | undefined;
  if (pending) return await pending;
  const promise = (async () => {
    const resp = await getPsychologistWorkSchedules({ take: 200 });
    type ApiSchedule = { id: string | number; weekday: number; start_time?: string; end_time?: string; active?: boolean; schedule_type?: string };
    const apiData = (resp as { data: ApiSchedule[] }).data || [];
    const tpl: WorkSchedule[] = apiData.map((h) => {
      const startTimeUTC = h.start_time || '';
      const endTimeUTC = h.end_time || '';
      const startResult = utcTimeToLocalWithDayShift(startTimeUTC, timezone);
      const endTimeLocal = utcTimeToLocal(endTimeUTC, timezone);
      // Ajustar weekday: si la conversión UTC→local cruza medianoche, el día local es distinto al UTC
      const rawWeekday = mapBackendDayToWeekday(h.weekday);
      const adjustedWeekday = ((rawWeekday + startResult.dayShift) % 7 + 7) % 7;
      return {
        id: h.id,
        psychologistId,
        weekday: adjustedWeekday,
        startTime: startResult.time.slice(0, 5),
        endTime: endTimeLocal.slice(0, 5),
        active: Boolean(h.active),
        scheduleType: h.schedule_type === 'reschedule_only' ? 'reschedule_only' as const : 'standard' as const
      };
    });
    schedulesCacheByPsych.set(psychologistId, tpl);
    pendingFetches.delete(key);
    return tpl;
  })();
  pendingFetches.set(key, promise);
  return await promise;
}

export async function fetchPatientsOnce(psychologistId: string): Promise<Patient[]> {
  const cached = patientsCacheByPsych.get(psychologistId);
  if (cached) return cached;
  const key = `patients:${psychologistId}`;
  const pending = pendingFetches.get(key) as Promise<Patient[]> | undefined;
  if (pending) return await pending;
  const promise = (async () => {
    const [patientsResp, sessionDataResults] = await Promise.all([
      apiCoreAdapter.psychologistPatients(),
      fetchPatientFullDataOnce(psychologistId),
    ]);

    const patientsWithSessions = new Set<string>(
      sessionDataResults.filter(r => r.last_session_date != null).map(r => r.id)
    );

    type PatientDTO = { id: string; process: string; process_type?: string; name?: string; surname?: string; email?: string; phone_last_four?: string; timezone?: string; nationality?: string; hasNextSessionDate?: boolean; fullname?: string; is_urgent?: boolean; scheduling_eligible?: boolean };
    const patientsResults = (patientsResp as { results?: PatientDTO[]; metadata?: { total?: number } })?.results;
    const patientsData = (patientsResp as { data?: PatientDTO[]; total?: number })?.data;
    const patientOptions: PatientDTO[] = Array.isArray(patientsResults) ? patientsResults : (Array.isArray(patientsData) ? patientsData : []);
    const normalized: Patient[] = patientOptions.map((p) => ({
      name: (p.fullname || [p.name || '', p.surname || ''].filter(Boolean).join(' ').trim()) || (p.email || ''),
      email: p.email || '',
      phone: p.phone_last_four || '',
      id: p.id,
      process: p.process,
      processType: p.process_type || undefined,
      timezone: p.timezone,
      nationality: p.nationality,
      hasNextSessionDate: p.hasNextSessionDate === true ? true : (p.hasNextSessionDate === false ? false : undefined),
      hasPriorSessions: p.id ? patientsWithSessions.has(p.id) : undefined,
      isUrgent: p.is_urgent ?? false,
      schedulingEligible: p.scheduling_eligible ?? true,
    }));

    // Merge patients from session data not already in the active list (inactivos, etc.)
    const existingIds = new Set(normalized.map(p => p.id).filter(Boolean));
    for (const r of sessionDataResults as Array<Record<string, unknown>>) {
      const id = r.id as string | undefined;
      if (!id || existingIds.has(id)) continue;
      normalized.push({
        name: [r.name, r.surname].filter(Boolean).join(' ').trim() || '',
        email: (r.email as string) || '',
        id,
        process: (r.process_id as string) || id,
        hasNextSessionDate: false,
        hasPriorSessions: patientsWithSessions.has(id),
        schedulingEligible: (r.scheduling_eligible as boolean | undefined) ?? true,
      });
    }

    patientsCacheByPsych.set(psychologistId, normalized);
    pendingFetches.delete(key);
    return normalized;
  })();
  pendingFetches.set(key, promise);
  return await promise;
}

export async function fetchSessionsOnce(
  psychologistId: string,
  startOfWeek: Date,
  endOfWeek: Date,
  weekKey: string,
  specialty: string
): Promise<SessionWithPsychologist[]> {
  const cached = sessionsCacheByWeek.get(weekKey);
  if (cached) return cached;
  const key = `sessions:${weekKey}`;
  const pending = pendingFetches.get(key) as Promise<SessionWithPsychologist[]> | undefined;
  if (pending) return await pending;
  const promise = (async () => {
    // Usar siempre el rango que pide el caller (la semana que se está viendo, o el rango
    // amplio que pide SessionDetailsModal para chequear solapamientos al reprogramar).
    // Antes esta función ignoraba startOfWeek/endOfWeek y recalculaba un rango fijo
    // relativo a "hoy" (hoy-6 a hoy+60), lo que hacía desaparecer de la agenda semanal
    // cualquier sesión de más de 6 días atrás, aunque sí apareciera en diaria/mensual
    // (que sí usan el rango realmente visible).
    const toTwo = (n: number) => n.toString().padStart(2, '0');
    const toStartOfDayISO = (d: Date) => `${d.getFullYear()}-${toTwo(d.getMonth() + 1)}-${toTwo(d.getDate())} 00:00:00`;
    const toEndOfDayISO = (d: Date) => `${d.getFullYear()}-${toTwo(d.getMonth() + 1)}-${toTwo(d.getDate())} 23:59:59`;
    const fromIso = toStartOfDayISO(startOfWeek);
    const toIso = toEndOfDayISO(endOfWeek);
    const sessionsResp = await apiCoreAdapter.psychologistSessionsList({ from: fromIso, to: toIso, take: 200, status: 'active,incomplete,settled,cancelled,free' });
    const rawList = sessionsResp.data.length !== 0 ? sessionsResp.data : [];

    const external: SessionWithPsychologist[] = (rawList).map((item) => {
      const id = String(item.id ?? '');
      const dateStr = String(item.date ?? '');
      const patientsVal = item.patients as unknown;
      let patientName = 'Paciente';
      let patientEmail = 'paciente@example.com';
      let patientTimezone: string | undefined;
      if (Array.isArray(patientsVal) && patientsVal.length > 0 && patientsVal[0] && typeof patientsVal[0] === 'object') {
        const p0 = patientsVal[0];
        if (typeof p0.name === 'string' && p0.name) patientName = p0.name;
        if (typeof p0.email === 'string' && p0.email) patientEmail = p0.email;
        if (typeof p0.timezone === 'string' && p0.timezone) patientTimezone = p0.timezone;
      }
      const parts = isoToLocalParts(dateStr);

      // Extract process_id. The API sometimes returns `process` as a plain string
      // (the process ID) and sometimes as an object `{ id, type }` — normalize both,
      // same as apiCoreAdapter.getPatientTimezoneFromSessions. Missing this normalization
      // was the root cause of "Ver proceso" doing nothing from the week view: process_id
      // ended up holding the whole object instead of its id.
      const rawProcess = item.process as unknown as { id?: string } | string | undefined;
      const process_id = (typeof rawProcess === 'string' ? rawProcess : rawProcess?.id) || undefined;

      // Debug log to verify process_id extraction
      if (!process_id) {
        console.warn('⚠️ process_id not found in session', {
          sessionId: id,
          date: dateStr,
          process: item.process,
          item
        });
      }

      // Get duration and therapy type (specialty) from appointment_type or process.type.name.
      // NOTE: `specialty` (the fetchSessionsOnce parameter) is NOT per-session — it's a single
      // value fixed for the whole fetch, and the main caller (useCalendarData) doesn't even pass
      // it. Using it directly here made every session's therapy type wrong/undefined. Resolve it
      // per-item instead, the same way `duration` already is.
      let duration = 60; // Default duration in minutes
      let resolvedSpecialty: string | undefined;

      // Priority 1: Use appointment_type if available (new sessions)
      const appointment_type_id = item.appointment_type;
      if (appointment_type_id) {
        const apt = SESSION_APPOINTMENT_TYPES.find(a => a.id === appointment_type_id);
        if (apt) {
          if (apt.duration) duration = apt.duration;
          resolvedSpecialty = apt.name;
        }
      }
      // Priority 2: Use process.type.name (all sessions, including old ones)
      else {
        const process_info = item.process_info as { type_name?: string } | undefined;
        const process_type_name = process_info?.type_name;

        if (process_type_name) {
          resolvedSpecialty = process_type_name;
          const apt = SESSION_APPOINTMENT_TYPES.find(a => a.name === process_type_name);
          if (apt && apt.duration) {
            duration = apt.duration;
          }
        }
      }

      return {
        id,
        psychologistId,
        date: parts.date,
        time: parts.time,
        modality: 'online',
        status: item.status,
        patient: { name: patientName, email: patientEmail, timezone: patientTimezone },
        specialty: resolvedSpecialty || specialty,
        origISO: dateStr,
        duration,
        process_id,
        isFirst: item.isFirst,
      };
    });
    sessionsCacheByWeek.set(weekKey, external);
    pendingFetches.delete(key);
    return external;
  })();
  pendingFetches.set(key, promise);
  return await promise;
}


